<template>
  <div class="oring-assistant__container material-assistant-step3">
    <div class="oring-assistant__container__cards">
      <div class="card">
        <div class="card-content">
          <div class="card-content__title">
            <h3 class="h4 card-content__title-text">
              {{ $tv('Frontend.Title.Pressure', 'ORingConfiguratorImport') }}
            </h3>
            <span class="card-content__title-icon">
              <ActionInfoFilledIcon
                tabindex="0"
                @click="openPopup"
                @keydown.enter="openPopup"
              />
            </span>
            <PopupBase
              v-if="showPopup"
              :show="showPopup"
              @close-pop-up="closePopup"
              :size="popupSizeSmall"
            >
              <PopupContent
                :title="popupContent.title"
                :content="popupContent.content"
              />
            </PopupBase>
          </div>
          <p class="card-content__description">
            {{ $tv('Frontend.Message.Pressure', 'ORingConfiguratorImport') }}
          </p>
          <div class="card-content__container first">
            <label
              for="step3-pressure"
              id="step3-pressure-label"
            >
              {{ $tv('labelPressure', 'ORingConfigurator') }} [{{ oringCalculatorPsi ? $tv('psi', 'unitsOfMeasure') : $tv('bar', 'unitsOfMeasure') }}]
            </label>
            <NumericInput
              v-model="pressure"
              id="step3-pressure"
              aria-labelledby="step3-pressure-label"
              :min="metricRanges?.pressMin"
              :max="metricRanges?.pressMax"
              :decimals="1"
              :format="'n1'"
              :step="1"
              :spinners="true"
              new-style-with-spinner
            />
            <div class="input-info">
              <template v-if="metricRanges">
                {{ formatPressure(metricRanges.pressMin) }} - {{ formatPressure(metricRanges.pressMax) }}
              </template>
              <template
                v-else
              >
                &nbsp;
              </template>
            </div>
          </div>
          <div class="card-content__container">
            <label
              for="step3-gap-width"
              id="step3-gap-width-label"
            >
              {{ $tv('labelGapWidth', 'ORingConfigurator') }} [{{ oringCalculatorImperial ? $tv('inch', 'unitsOfMeasure'): $tv('mm', 'unitsOfMeasure') }}]
            </label>
            <NumericInput
              v-model="gapWidth"
              id="step3-gap-width"
              aria-labelledby="step3-gap-width-label"
              :min="metricRanges?.gapMin"
              :max="metricRanges?.gapMax"
              :spinners="true"
              :step="gapStep"
              :decimals="gapDecimals"
              :format="gapFormat"
              new-style-with-spinner
            />
            <div class="input-info">
              <template v-if="metricRanges">
                {{ formatGapWidth(metricRanges.gapMin) }} - {{ formatGapWidth(metricRanges.gapMax) }}
              </template>
              <div
                v-else
              >
                &nbsp;
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import NumericInput from '~/components/input-components/NumericInput.vue'
import {useOringCalculatorService} from '@/composables/oring_calc_service'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import useI18nExtended from '~/composables/usei18nExtended'
import ActionInfoFilledIcon from '~/assets/icons/action_info_filled.svg?component'
import PopupBase from '~/components/popup/PopupBase.vue'
import PopupContent from '../PopupContent.vue'
import {POPUP} from '~/assets/js/constants'

const store = useORingCalculatorStore()
const {oringMaterialAssistantMetricRanges, oringCalculatorImperial, oringCalculatorPsi, oringCalculatorFahrenheit, oringMaterialAssistantGapWidth, oringMaterialAssistantPressure} = storeToRefs(store)
const metricRanges = computed(() => oringMaterialAssistantMetricRanges.value)
const gapWidth = oringMaterialAssistantGapWidth
const pressure = oringMaterialAssistantPressure
const i18n = useI18nExtended()

const popupSizeSmall = POPUP.Size.small
const showPopup = ref(false)
const popupContent = {
  title: i18n.tv('Frontend.Title.Pressure', 'ORingConfiguratorImport'),
  content: i18n.tv('Frontend.Info.Pressure', 'ORingConfigurator')
}
function openPopup() {
  showPopup.value = true
}
function closePopup() {
  showPopup.value = false
}

function formatPressure(val: number) {
  if (val === null || val === undefined) {return ''}
  const unit = oringCalculatorPsi.value ? i18n.tv('psi', 'unitsOfMeasure') : i18n.tv('bar', 'unitsOfMeasure')
  return val.toLocaleString(undefined, {minimumFractionDigits: 1, maximumFractionDigits: 1}) + ' ' + unit
}

const gapDecimals = computed(() => oringCalculatorImperial.value ? 4 : 3)
const gapStep = computed(() => oringCalculatorImperial.value ? 0.01 : 0.1)
const gapFormat = computed(() => oringCalculatorImperial.value ? 'n4' : 'n3')

function formatGapWidth(val: number) {
  if (val === null || val === undefined) {return ''}
  const decimals = oringCalculatorImperial.value ? 4 : 3
  const unit = oringCalculatorImperial.value ? i18n.tv('inch', 'unitOfMeasurement') : i18n.tv('mm', 'unitOfMeasurement')
  return val.toLocaleString(undefined, {minimumFractionDigits: decimals, maximumFractionDigits: decimals}) + ' ' + unit
}

async function fetchMetricRanges() {
  const {getMetricRangesMaterialAssistant} = useOringCalculatorService()
  oringMaterialAssistantMetricRanges.value = await getMetricRangesMaterialAssistant({
    language: i18n.locale.value,
    imperial: oringCalculatorImperial.value,
    fahrenheit: oringCalculatorFahrenheit.value
  })
}

onMounted(async () => {
  if (!oringMaterialAssistantMetricRanges.value) {
    await fetchMetricRanges()
  }
  // eslint-disable-next-line eqeqeq
  if (gapWidth.value == null) {gapWidth.value = 0.01}
  // eslint-disable-next-line eqeqeq
  if (pressure.value == null) {pressure.value = 5}
})

watch([oringCalculatorImperial, oringCalculatorFahrenheit], async ([newImperial, newFahrenheit], [oldImperial, oldFahrenheit]) => {
  if (newImperial !== oldImperial || newFahrenheit !== oldFahrenheit) {
    await fetchMetricRanges()
  }
})

function validate() {
  const metricRanges = oringMaterialAssistantMetricRanges.value
  return (
    metricRanges &&
    gapWidth.value !== null &&
    pressure.value !== null &&
    gapWidth.value >= metricRanges.gapMin &&
    gapWidth.value <= metricRanges.gapMax &&
    pressure.value >= metricRanges.pressMin &&
    pressure.value <= metricRanges.pressMax
  )
}
defineExpose({validate})
</script>

<style lang="scss">
.material-assistant-step3 {
  .numeric-input-container {
    max-width: 11.6875rem;
  }

  .card-content__container.first {
    margin-bottom: 1rem;
  }

  .card-content__description {
    margin: 0;
  }

  label {
    font-size: .875rem;
    font-weight: $base-text-font-weight-light;
    margin-bottom: .375rem;
  }

  .input-info {
    margin-top: .25rem;
    font-size: .75rem;
    font-weight: $base-text-font-weight-light;
  }
}
</style>
